import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '@algoan/dashboard/store/store';
import {
  Analysis,
  AnalysisBounds,
  AsyncStatus,
  AnalysisInformation,
  Transaction,
  AnalysisConfigurationMode,
  WindowConfiguration,
  WindowConfigurationMode,
  CashFlowConfig,
  AnalysesErrorMessages,
  Essentials,
} from '@algoan/dashboard/model';
import { customerSelector } from './customer-selectors';
import { selectDate, convertStringToDate } from '@algoan/dashboard/utils';
import { compareAsc, differenceInCalendarDays } from 'date-fns';
import { analysisWindowConfigSelector } from './config-selectors';
import { associateTransactionWithCashFlow, formatAnalysisBounds } from '@algoan/dashboard/store/utils';

export const analysesStatusSelector = (state: RootState): AsyncStatus => state.analysis.analysesFetchingStatus;

export const isAnalysesFetchingSelector: (state: RootState) => boolean = createSelector(
  analysesStatusSelector,
  (analysesStatus: AsyncStatus): boolean =>
    analysesStatus === AsyncStatus.IDLE || analysesStatus === AsyncStatus.PENDING,
);

export const analysesErrorSelector = (state: RootState): AnalysesErrorMessages | undefined =>
  state.analysis.analysesError;

export const analysesErrorDateSelector = (state: RootState): string | undefined => state.analysis.analysesErrorDate;

export const analysesErrorMessageSelector = (state: RootState): string | undefined => {
  return state.analysis.analysesErrorMessage;
};

export const analysesSelector = (state: RootState): Analysis[] => state.analysis.analyses;

export const latestAnalysisSelector: (state: RootState) => Analysis | undefined = createSelector(
  analysesSelector,
  (analyses: Analysis[]): Analysis | undefined => (analyses.length > 0 ? analyses[0] : undefined),
);

export const latestAnalysisConfigSelector: (
  state: RootState,
  customerId: string,
) => Record<string, CashFlowConfig> | undefined = createSelector(
  latestAnalysisSelector,
  (state: RootState, customerId: string) => customerSelector(customerId)(state),
  (latestAnalysis, customer): Record<string, CashFlowConfig> | undefined =>
    latestAnalysis && customer ? customer.dashboard.analysesConfig?.[latestAnalysis.id] : undefined,
);

export const analysisBoundsSelector: (state: RootState) => AnalysisBounds | undefined = createSelector(
  (state: RootState) => latestAnalysisSelector(state)?.metadata,
  (metadata): AnalysisBounds | undefined => {
    const calendar = metadata?.creditInsights?.calendar ?? metadata?.calendar;
    const minWindowDate = metadata?.creditInsights?.minWindowDate;

    if (!Array.isArray(calendar) || calendar.length === 0) {
      return undefined;
    }

    return formatAnalysisBounds(calendar, minWindowDate);
  },
);

export const analysisInformationSelector: (state: RootState) => AnalysisInformation = createSelector(
  (state: RootState): Transaction[] => state.transaction.transactions,
  latestAnalysisSelector,
  analysisWindowConfigSelector,
  (transactions, analysis, windowConfig): AnalysisInformation => {
    const sortedTransactions = [...transactions];
    sortedTransactions.sort((a, b) => compareAsc(new Date(selectDate(a.dates)), new Date(selectDate(b.dates))));

    const totalDays =
      differenceInCalendarDays(
        new Date(selectDate(sortedTransactions[sortedTransactions.length - 1]?.dates)),
        new Date(selectDate(sortedTransactions[0]?.dates)),
      ) + 1;

    const analysisMode = getAnalysisMode(analysis, windowConfig);
    const totalDaysOfAnalysis =
      analysisMode === AnalysisConfigurationMode.DAYS
        ? analysis?.parameters?.period?.numberOfDays || 90
        : analysis?.metadata?.creditInsights?.numberOfDaysInWindow;

    return {
      aggregationPeriodStart: convertStringToDate(selectDate(sortedTransactions[0]?.dates)),
      aggregationPeriodEnd: convertStringToDate(selectDate(sortedTransactions[sortedTransactions.length - 1]?.dates)),
      totalDaysForAggregationPeriod: totalDays || 0,
      totalDaysOfAnalysis: Math.min(totalDaysOfAnalysis ?? totalDays, totalDays || 0), // totalDaysOfAnalysis cannot be > totalDaysForAggregationPeriod
      analysisMode,
      maxWindowDate: convertStringToDate(analysis?.metadata?.creditInsights?.maxWindowDate),
      minWindowDate: convertStringToDate(analysis?.metadata?.creditInsights?.minWindowDate),
      referenceDate: convertStringToDate(analysis?.metadata?.referenceDate),
    };
  },
);

export const transactionIdToCashFlowIdSelector: (state: RootState) => { [key: string]: string } = createSelector(
  latestAnalysisSelector,
  (analysis): { [key: string]: string } => associateTransactionWithCashFlow(analysis?.creditInsights?.cashFlows),
);

/**
 * If the analysis contains the mode, returns it. Otherwise (case of a new analysis), returns the mode defined in the
 * configuration of the product CREDIT_INSIGHTS.
 *
 * The configuration mode not handled by the Dashboard are treated as the mode DAYS.
 *
 */
const getAnalysisMode = (
  analysis: Analysis | undefined,
  windowConfig: WindowConfiguration | undefined,
): AnalysisConfigurationMode => {
  if (analysis?.parameters?.period?.mode) {
    return analysis.parameters.period.mode;
  }

  switch (windowConfig?.mode) {
    case WindowConfigurationMode.MONTHS_TRUNCATED:
      return AnalysisConfigurationMode.MONTHS_TRUNCATED;
    case WindowConfigurationMode.DAYS:
    default:
      return AnalysisConfigurationMode.DAYS;
  }
};

export const activityScoreSelector: (state: RootState) => number | undefined = createSelector(
  latestAnalysisSelector,
  (analysis): number | undefined => {
    return analysis?.metadata?.activityScore?.value;
  },
);

export const essentialsSelector = (state: RootState): Essentials | undefined => state.analysis.essentials;

export const essentialsFetchingStatusSelector = (state: RootState): AsyncStatus =>
  state.analysis.essentialsFetchingStatus;

export const isEssentialsFetchingSelector: (state: RootState) => boolean = createSelector(
  essentialsFetchingStatusSelector,
  (status: AsyncStatus): boolean => status === AsyncStatus.IDLE || status === AsyncStatus.PENDING,
);
